import { Box, Center, Flex, Heading, Icon, Text } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { Accept, FileWithPath, useDropzone } from 'react-dropzone';

import { truncateUploadFileName } from 'packages/common-frontend/utils';
import { FiFileMinus, FiFilePlus, FiUpload } from 'react-icons/fi';

export const UploadDocumentFileExtensions = ['docx'] as const;
// order is significant here since the first filetype will be the default selected
// filetype when uploading via file dialog. use xlsx first since that's more
// relevant to report templates
export const UploadSpreadsheetFileExtensions = ['xlsx', 'csv'] as const;
export type UploadFileExtensionType =
  | typeof UploadDocumentFileExtensions[number]
  | typeof UploadSpreadsheetFileExtensions[number];

type UploadTemplateDropzoneProps = {
  allowedExtensions: readonly UploadFileExtensionType[];
  isDisabled: boolean;
  setFieldValue: (value: FileWithPath) => void;
  accept: Accept;
  multiple?: boolean;
};
export const UploadTemplateDropzone = ({
  allowedExtensions,
  isDisabled,
  setFieldValue,
  accept,
  multiple = false,
}: UploadTemplateDropzoneProps) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept,
    multiple,
    disabled: isDisabled,
    onDrop: useCallback((files: FileWithPath[]) => setFieldValue(files[0]), [
      setFieldValue,
    ]),
  });

  const fileSelected = acceptedFiles.length > 0 || fileRejections.length > 0;

  return (
    <Box width="100%" cursor="pointer" id="upload-template-dropzone">
      <Center
        border="1px"
        borderRadius="md"
        minHeight="200px"
        flexDirection="column"
        {...getRootProps({ className: 'dropzone' })}
      >
        <input {...getInputProps()} />

        {acceptedFiles.length > 0 && (
          <>
            <Heading size="sm">
              <Icon as={FiFilePlus} color="green.300" /> Accepted
            </Heading>
            {acceptedFiles.map(({ path }: FileWithPath) => (
              <Text key={path}>{truncateUploadFileName(path)}</Text>
            ))}
          </>
        )}

        {fileRejections.length > 0 && (
          <>
            <Flex alignItems="center">
              <Icon as={FiFileMinus} color="red.300" />
              <Heading size="sm">Rejected</Heading>
            </Flex>
            {fileRejections.map(({ file }: { file: FileWithPath }) => (
              <Box key={file.path}>
                <Text>{truncateUploadFileName(file.path)}</Text>
                <Text>
                  Expected file of type <em>csv</em>
                </Text>
              </Box>
            ))}
          </>
        )}

        <Flex
          justifyContent="space-between"
          alignItems="center"
          pt={fileSelected ? '5' : '0'}
        >
          <Flex direction="column" alignItems="center">
            <Text as={fileSelected ? 'sub' : 'i'} mb={fileSelected ? '4' : '0'}>
              Drag a file here, or click to upload.
            </Text>
            <Text as={fileSelected ? 'sub' : 'i'}>
              Allowed file types:{' '}
              {allowedExtensions.map((type) => `.${type}`).join(', ')}.
            </Text>
          </Flex>
          <Flex ml="3">
            <FiUpload />
          </Flex>
        </Flex>
      </Center>
    </Box>
  );
};
